import type { ComponentProps } from 'react'

import { Dialog as SheetPrimitive } from 'radix-ui'
import { cn } from '@/lib/utils'

function Sheet(props: ComponentProps<typeof SheetPrimitive.Root>) {
    return <SheetPrimitive.Root data-slot='sheet' {...props} />
}

function SheetTrigger(props: ComponentProps<typeof SheetPrimitive.Trigger>) {
    return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />
}

function SheetClose(props: ComponentProps<typeof SheetPrimitive.Close>) {
    return <SheetPrimitive.Close data-slot='sheet-close' {...props} />
}

function SheetOverlay({ className, ...props }: ComponentProps<typeof SheetPrimitive.Overlay>) {
    return (
        <SheetPrimitive.Overlay
            data-slot='sheet-overlay'
            className={cn(
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:ease-power3-in data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:ease-power3-out fixed inset-0 z-50 bg-black/60 backdrop-blur-[6px] duration-350 data-[state=closed]:duration-250',
                className
            )}
            {...props}
        />
    )
}

type SheetContentProps = ComponentProps<typeof SheetPrimitive.Content> & {
    side?: 'left' | 'right' | 'bottom'
}

function SheetContent({
    className,
    children,
    side = 'right',
    ...props
}: SheetContentProps) {
    return (
        <SheetPrimitive.Portal>
            <SheetOverlay />
            <SheetPrimitive.Content
                data-slot='sheet-content'
                className={cn(
                    'bg-background text-foreground data-[state=closed]:animate-out data-[state=closed]:ease-power3-in data-[state=open]:animate-in data-[state=open]:ease-power3-out fixed z-50 flex w-full flex-col duration-350 outline-none data-[state=closed]:duration-250',
                    side === 'right' && 'border-line-strong data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-dvh border-l',
                    side === 'left' && 'border-line-strong data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-dvh border-r',
                    side === 'bottom' && 'border-line-strong shadow-overlay data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl border-t',
                    className
                )}
                {...props}
            >
                {children}
            </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
    )
}

function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='sheet-header'
            className={cn(
                'border-line-strong flex h-14 shrink-0 items-center justify-between gap-2 border-b px-6',
                className
            )}
            {...props}
        />
    )
}

function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='sheet-footer'
            className={cn(
                'border-line-strong flex h-[62px] shrink-0 items-center justify-between gap-2 border-t px-6',
                className
            )}
            {...props}
        />
    )
}

function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
    return (
        <SheetPrimitive.Title
            data-slot='sheet-title'
            className={cn('lead-style', className)}
            {...props}
        />
    )
}

function SheetDescription({ className, ...props }: ComponentProps<typeof SheetPrimitive.Description>) {
    return (
        <SheetPrimitive.Description
            data-slot='sheet-description'
            className={cn('caption-style text-soft', className)}
            {...props}
        />
    )
}

export {
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription
}
